import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LiliButton } from '@lili/lili-frontend-common';
import Heading from '../../../generic-components/Heading';
import { MoveMoneySteps, purposeOfTransferContentMap } from '../consts';
import { VIEWS_MAP } from '../../../router/constants';
import { useStore } from '../../../store';
import { NavigationHeader } from '../components/NavigationHeader';
import { LiliTextArea } from '../../../DesignSystem/LiliTextArea/LiliTextArea';

enum PURPOSE_VALUES_TO_TRIGGER_CUSTOM_REASON {
  OTHER = 'Other',
  OTHER_BUSINESS_EXPENSES = 'Other business expenses',
}

export const PurposeOfTransfer = () => {
  const { moveType } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollableContainer = useRef<HTMLDivElement>(null);

  const { OTHER, OTHER_BUSINESS_EXPENSES } = PURPOSE_VALUES_TO_TRIGGER_CUSTOM_REASON;

  const moveMoneyData = useStore((state) => state.moveMoneyData);
  const setMoveMoneyData = useStore((state) => state.setMoveMoneyData);
  const resetMoveMoneyData = useStore((state) => state.resetMoveMoneyData);

  const [purposeOfTransfer, setPurposeOfTransfer] = useState<string>(moveMoneyData.purposeOfTransfer || '');
  const [customPurposeOfTransferValue, setCustomPurposeOfTransferValue] = useState<string>(moveMoneyData.purposeOfTransferReason || '');

  const minCharsForOtherReason = 5;

  const maxCharsForOtherReason = 140;

  const shouldShowCustomPurposeOfTransfer = useMemo(() => {
    const valuesToShowCustomPurposeOfTransfer = [OTHER, OTHER_BUSINESS_EXPENSES];
    const isPurposeIncluded = valuesToShowCustomPurposeOfTransfer.includes(purposeOfTransfer as PURPOSE_VALUES_TO_TRIGGER_CUSTOM_REASON);

    setTimeout(() => {
      if (isPurposeIncluded && scrollableContainer.current) {
        scrollableContainer.current.scrollTop = scrollableContainer.current.scrollHeight;
      }
    }, 10);

    return isPurposeIncluded;
  }, [purposeOfTransfer]);

  const handleCloseFlow = () => {
    resetMoveMoneyData();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleBackButton = () => {
    setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.AMOUNT });
  };

  const handleContinue = () => {
    const nextStepId = moveMoneyData.eddRequired
      ? MoveMoneySteps.INTERNATIONAL_WIRE_EDD
      : MoveMoneySteps.REVIEW;
    if (shouldShowCustomPurposeOfTransfer) {
      setMoveMoneyData({
        ...moveMoneyData,
        purposeOfTransfer,
        purposeOfTransferReason: customPurposeOfTransferValue,
        activeStepId: nextStepId,
      });
    } else {
      setMoveMoneyData({
        ...moveMoneyData,
        purposeOfTransfer,
        activeStepId: nextStepId,
      });
    }
  };

  const isSubmitDisabled = useMemo(() => {
    if (shouldShowCustomPurposeOfTransfer && (customPurposeOfTransferValue.length < minCharsForOtherReason || customPurposeOfTransferValue.length > maxCharsForOtherReason)) {
      return true;
    }
    return !purposeOfTransfer;
  }, [purposeOfTransfer, customPurposeOfTransferValue]);

  return (
    <div className='fixed overflow-auto left-0 top-0 w-full h-full bg-bw01 z-50 flex flex-col'>
      <NavigationHeader hideBackButton={!!moveMoneyData.billId} onBack={handleBackButton} onClose={handleCloseFlow} />

      <Heading
        type='HTML'
        title={t(purposeOfTransferContentMap[moveType as keyof typeof purposeOfTransferContentMap].title)}
        className='mx-auto text-center mb-[12px] font-metricMedium text-bw08 text-[24px]'
      />
      <Heading
        type='HTML'
        title={`To ${moveMoneyData.vendorData?.name}`}
        className='mx-auto text-center mb-[22px] font-metricR text-bw08 text-[16px]'
      />

      <div ref={scrollableContainer} className='flex flex-col items-center gap-[4px] w-[395px] pt-[10px] overflow-scroll pb-[110px] grow mx-auto no-scrollbar no-scrollbar::-webkit-scrollbar'>
        {moveMoneyData.purposes.map((option) => (
          <div className='flex flex-col w-full gap-[4px]'>
            <div
              className={`border ${purposeOfTransfer === option ? 'border-action03' : 'border-bw04'} hover:border-action03 rounded-[8px] w-full`}
            >
              <button onClick={() => setPurposeOfTransfer(option)} type='button' className='w-full'>
                <Heading type='HTML' title={option} className='font-metricR text-bw08 text-start text-[16px] p-3' />
              </button>
            </div>
            {shouldShowCustomPurposeOfTransfer && purposeOfTransfer === option && <LiliTextArea
              minLength={5}
              maxLength={maxCharsForOtherReason}
              placeholder={t(
                purposeOfTransferContentMap[moveType as keyof typeof purposeOfTransferContentMap]
                  .other_option_placeholder,
              )}
              onValueChange={(value) => setCustomPurposeOfTransferValue(value)}
              value={customPurposeOfTransferValue}
            />}
          </div>

        ))}

      </div>

      <div
        className='absolute bg-bw00/80 left-0 bottom-0 w-full border-t border-bw03 flex items-center justify-center py-[24px]'
      >
        <LiliButton
          disabled={isSubmitDisabled}
          onClick={handleContinue}
          id='continue'
          className='w-[375px]'
          title={t('MOVE_MONEY.STEPS.PURPOSE_OF_TRANSFER.CTA')}
        />
      </div>
    </div>
  );
};
